import { createForm, useStore } from "@tanstack/solid-form";
import { AiFillPlusCircle } from "solid-icons/ai";
import { IoPencilSharp, IoTrash } from "solid-icons/io";
import {
  type Component,
  createMemo,
  createSignal,
  For,
  type JSX,
  type ParentComponent,
  Show,
} from "solid-js";
import { produce } from "solid-js/store";
import { toast } from "solid-sonner";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
  Input,
} from "@/components/ui/input";
import { Switch, SwitchControl, SwitchThumb } from "@/components/ui/switch";
import { TextArea } from "@/components/ui/textarea";
import {
  TextFieldDescription,
  TextFieldErrorMessage,
  TextFieldLabel,
  TextFieldRoot,
} from "@/components/ui/textfield";
import type { OnboardingEducation } from "../../context/OnboardingContext";
import { useOnboardingStore } from "../../context/OnboardingContext";
import { OnboardingDescription, OnboardingTitle } from "../shared/text";
import { sortEducationByStartDate } from "../utils";

const EducationPreviewItem: Component<OnboardingEducation> = (props) => {
  const { t } = useI18n();
  const [, setStore] = useOnboardingStore();

  const handleDelete = () => {
    setStore(
      produce((draft) => {
        const updatedEducation = draft.education.value.filter(
          (education) => education.temp_id !== props.temp_id,
        );
        draft.education.value = sortEducationByStartDate(updatedEducation);
      }),
    );
  };

  const startDate = createMemo(() =>
    props.start_date ? new Date(props.start_date).toLocaleDateString() : "",
  );
  const endDate = createMemo(() => {
    if (props.end_date) {
      if (props.is_current) {
        return "Present";
      }
      return new Date(props.end_date).toLocaleDateString();
    }
    return "Present";
  });
  return (
    <Card class="flex flex-col gap-4 shrink-0 w-full bg-white border-primary/10 rounded-lg relative shadow-none min-h-64 max-w-md min-w-md">
      <CardHeader class="max-w-xs">
        <CardTitle>{props.institution}</CardTitle>
        <CardDescription>
          {props.field_of_study} | {startDate()} - {endDate()}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Show when={props.description}>
          <p class="text-sm text-gray-500">{props.description}</p>
        </Show>
        <div class="flex gap-2 items-center justify-center absolute top-4 right-4">
          <EducationDialog
            education={{
              temp_id: props.temp_id,
              institution: props.institution,
              degree_type: props.degree_type,
              field_of_study: props.field_of_study,
              start_date: props.start_date ? new Date(props.start_date) : null,
              end_date: props.end_date ? new Date(props.end_date) : null,
              is_current: props.is_current,
              description: props.description,
            }}
          >
            <Button variant="outline">
              <IoPencilSharp class="w-4 h-4" />
              <span class="sr-only">{t("common.edit")}</span>
            </Button>
          </EducationDialog>
          <Button variant="outline" onClick={handleDelete}>
            <IoTrash class="w-4 h-4" />
            <span class="sr-only">{t("common.delete")}</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const EmptyEducationPreviewItem = () => {
  const { t } = useI18n();
  return (
    <EducationDialog>
      <Card class="flex gap-4 max-w-md w-full bg-white group cursor-pointer hover:bg-white border-dashed border-gray-400 rounded-lg relative min-h-64 shadow-none transition-colors duration-300">
        <CardContent class="flex flex-col items-start justify-center w-full">
          <Button variant="link" class="p-0">
            <AiFillPlusCircle class="w-8 h-8 group-hover:text-primary/70" />
            <span class="sr-only">{t("common.add")}</span>
          </Button>
          <p class="text-2xl text-gray-500 text-center mt-2">
            {t("onboarding.steps.education.addEducation")}
          </p>
        </CardContent>
      </Card>
    </EducationDialog>
  );
};
export const Education = () => {
  const [store] = useOnboardingStore();
  const { t } = useI18n();
  const sortedEducation = createMemo(() =>
    sortEducationByStartDate(store.education.value),
  );
  return (
    <div class="flex flex-col gap-8 w-full">
      <div class="flex flex-col gap-4">
        <OnboardingTitle>
          {t("onboarding.steps.education.title")}
        </OnboardingTitle>
        <OnboardingDescription>
          {t("onboarding.steps.education.description")}
        </OnboardingDescription>
      </div>
      <div class="w-full relative">
        <div class="overflow-x-auto flex gap-4 w-full">
          <Show
            when={sortedEducation().length > 0}
            fallback={<EmptyEducationPreviewItem />}
          >
            <For each={sortedEducation()}>
              {(education) => (
                <EducationPreviewItem
                  temp_id={education.temp_id}
                  institution={education.institution}
                  start_date={education.start_date}
                  end_date={education.end_date}
                  degree_type={education.degree_type}
                  field_of_study={education.field_of_study}
                  is_current={!!education.is_current}
                  description={education.description}
                />
              )}
            </For>
            <Show when={sortedEducation().length > 0}>
              <div class="hidden lg:block absolute top-1/2 left-0 -translate-x-16  z-10">
                <EducationDialog>
                  <Button variant="link" class="p-0 w-fit h-fit group">
                    <AiFillPlusCircle class="w-12 h-12 group-hover:text-primary/70 transition-colors duration-300" />
                    <span class="sr-only">{t("common.add")}</span>
                  </Button>
                </EducationDialog>
              </div>
            </Show>
          </Show>
        </div>
        <div class="lg:hidden mt-4">
          <EducationDialog>
            <Button>{t("onboarding.steps.education.addEducation")}</Button>
          </EducationDialog>
        </div>
      </div>
    </div>
  );
};

const educationDialogSchema = z.object({
  institution: z
    .string({ error: "Scuola/Università obbligatoria" })
    .min(1, { error: "Scuola/Università obbligatoria" }),
  degree_type: z.string().optional(),
  field_of_study: z.string().optional(),
  start_date: z.date().min(1, { error: "Data di inizio obbligatoria" }),
  end_date: z.date().optional().or(z.null()),
  description: z.string().optional().or(z.null()),
  is_current: z.boolean().optional(),
});

const EducationDialog: ParentComponent<{
  children: JSX.Element;
  education?: OnboardingEducation;
}> = (props) => {
  const [, setStore] = useOnboardingStore();
  const isEditing = props.education !== undefined;
  const [isOpen, setIsOpen] = createSignal(false);
  const form = createForm(() => ({
    defaultValues: {
      ...props.education,
      start_date: props.education?.start_date
        ? props.education?.start_date
        : null,
      end_date: props.education?.end_date ? props.education?.end_date : null,
    },

    onSubmit: async ({ value }) => {
      const validatedData = educationDialogSchema.safeParse(value);
      if (!validatedData.success) {
        return;
      }
      if (isEditing) {
        toast.success(
          t("common.toast.updated_success", {
            item: t("onboarding.steps.education.form.institution"),
          }),
        );
        setStore(
          produce((draft) => {
            const oldEducation = draft.education.value.find(
              (education) => education.temp_id === props.education?.temp_id,
            );
            if (oldEducation) {
              oldEducation.institution = validatedData.data.institution;
              oldEducation.degree_type = validatedData.data.degree_type;
              oldEducation.field_of_study = validatedData.data.field_of_study;
              oldEducation.start_date = validatedData.data.start_date;
              oldEducation.end_date = validatedData.data.end_date ?? null;
              oldEducation.is_current = validatedData.data.is_current ?? false;
              oldEducation.description = validatedData.data.description;
              oldEducation.temp_id = props.education!.temp_id!;
            }
            const updatedEducation = draft.education.value.map((education) =>
              education.temp_id === props.education?.temp_id
                ? oldEducation!
                : education,
            );
            draft.education.value = sortEducationByStartDate(updatedEducation);
          }),
        );
      } else {
        toast.success(
          t("common.toast.added_success", {
            item: t("onboarding.steps.education.form.institution"),
          }),
        );
        setStore(
          produce((draft) => {
            const newEducation = {
              ...validatedData.data,
              temp_id: (draft.education.value.length + 1).toString(),
              start_date: validatedData.data.start_date,
              end_date: validatedData.data.end_date ?? null,
              is_current: validatedData.data.is_current ?? false,
            };

            const updatedEducation = [...draft.education.value, newEducation];
            draft.education.value = sortEducationByStartDate(updatedEducation);
          }),
        );
      }
      setIsOpen(false);
      form.reset();
    },
  }));
  const { t } = useI18n();

  const isCurrent = useStore(form.store, (state) => state.values.is_current);
  const endDate = useStore(form.store, (state) => state.values.end_date);
  const startDate = useStore(form.store, (state) => state.values.start_date);

  return (
    <Dialog
      open={isOpen()}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          form.reset();
        }
      }}
    >
      <DialogTrigger class="lg:w-full">{props.children}</DialogTrigger>
      <DialogContent class="max-w-2xl min-h-[min(600px,90svh)] bg-card">
        <DialogHeader>
          <DialogTitle class="text-2xl font-medium">
            <Show
              when={isEditing}
              fallback={t("onboarding.steps.education.addEducation")}
            >
              {t("onboarding.steps.education.editEducation")}
            </Show>
          </DialogTitle>
        </DialogHeader>
        <div class="flex flex-col gap-4">
          <form.Field
            name="institution"
            validators={{
              onChange: educationDialogSchema.shape.institution,
              onBlur: educationDialogSchema.shape.institution,
            }}
          >
            {(field) => {
              return (
                <FieldContainer>
                  <FieldContainerLabel for="institution">
                    {t("onboarding.steps.education.form.institution")}
                  </FieldContainerLabel>
                  <Input
                    id="institution"
                    autocomplete="institution"
                    name={field().name}
                    value={field().state.value ?? ""}
                    onBlur={field().handleBlur}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                  />
                  <FieldInfo
                    field={field()}
                    helpText={t(
                      "onboarding.steps.education.form.institutionHelp",
                    )}
                  />
                </FieldContainer>
              );
            }}
          </form.Field>
          <form.Field
            name="degree_type"
            validators={{
              onChange: educationDialogSchema.shape.degree_type,
              onBlur: educationDialogSchema.shape.degree_type,
            }}
          >
            {(field) => {
              return (
                <FieldContainer>
                  <FieldContainerLabel for="degree_type">
                    {t("onboarding.steps.education.form.degreeType")}
                  </FieldContainerLabel>
                  <Input
                    autocomplete="degree_type"
                    id="degree_type"
                    name={field().name}
                    value={field().state.value ?? ""}
                    onBlur={field().handleBlur}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                  />
                  <FieldInfo
                    field={field()}
                    helpText={t(
                      "onboarding.steps.education.form.degreeTypeHelp",
                    )}
                  />
                </FieldContainer>
              );
            }}
          </form.Field>
          <form.Field
            name="field_of_study"
            validators={{
              onChange: educationDialogSchema.shape.field_of_study,
              onBlur: educationDialogSchema.shape.field_of_study,
            }}
          >
            {(field) => {
              return (
                <FieldContainer>
                  <FieldContainerLabel for="field_of_study">
                    {t("onboarding.steps.education.form.fieldOfStudy")}
                  </FieldContainerLabel>
                  <Input
                    autocomplete="field_of_study"
                    id="field_of_study"
                    name={field().name}
                    value={field().state.value ?? ""}
                    onBlur={field().handleBlur}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                  />
                  <FieldInfo
                    field={field()}
                    helpText={t(
                      "onboarding.steps.education.form.fieldOfStudyHelp",
                    )}
                  />
                </FieldContainer>
              );
            }}
          </form.Field>

          <div class="grid grid-cols-2 gap-4 w-full">
            <form.Field
              name="start_date"
              validators={{
                onChange: ({ value }) => {
                  if (value) {
                    if (endDate()) {
                      const startDateObj = value;
                      const endDateObj = endDate()!;
                      if (startDateObj > endDateObj) {
                        return t(
                          "onboarding.steps.education.form.startDateHelp",
                        );
                      }
                    }
                  }
                },
                onBlur: ({ value }) => {
                  const validatedData =
                    educationDialogSchema.shape.start_date.safeParse(value);
                  if (!validatedData.success) {
                    return validatedData.error.issues[0].message;
                  }
                  return undefined;
                },
              }}
            >
              {(field) => {
                return (
                  <FieldContainer>
                    <FieldContainerLabel for="start_date">
                      {t("onboarding.steps.education.form.startDate")}
                    </FieldContainerLabel>
                    <DatePicker
                      id="start_date"
                      value={field().state.value ?? undefined}
                      maxDate={endDate() ?? undefined}
                      onChange={(date) => {
                        if (date) {
                          field().handleChange(date);
                        }
                      }}
                      classList={{
                        "[&_button]:border-red-500":
                          field().state.meta.errors.length > 0,
                      }}
                    />
                    <FieldInfo
                      field={field()}
                      helpText={t(
                        "onboarding.steps.education.form.startDateHelp",
                      )}
                    />
                  </FieldContainer>
                );
              }}
            </form.Field>
            <form.Field
              name="end_date"
              validators={{
                onChange: ({ value }) => {
                  if (value) {
                    if (startDate()) {
                      const startDateObj = startDate()!;
                      const endDateObj = value;
                      if (endDateObj < startDateObj) {
                        return t("onboarding.steps.education.form.endDateHelp");
                      }
                    }
                  }
                },
              }}
            >
              {(field) => {
                return (
                  <FieldContainer>
                    <FieldContainerLabel for="end_date">
                      {t("onboarding.steps.education.form.endDate")}
                    </FieldContainerLabel>
                    <DatePicker
                      id="end_date"
                      disabled={isCurrent()}
                      value={field().state.value ?? undefined}
                      minDate={startDate() ?? undefined}
                      onChange={(date) => {
                        if (date) {
                          field().handleChange(date);
                        }
                      }}
                      classList={{
                        "[&_button]:border-red-500":
                          field().state.meta.errors.length > 0,
                      }}
                    />
                    <FieldInfo
                      field={field()}
                      helpText={t(
                        "onboarding.steps.education.form.endDateHelp",
                      )}
                    />
                  </FieldContainer>
                );
              }}
            </form.Field>
          </div>

          <form.Field name="is_current">
            {(field) => (
              <div class="flex items-center gap-2 w-full">
                <Switch
                  name={field().name}
                  checked={field().state.value ?? false}
                  onChange={(checked) => {
                    field().handleChange(checked ?? false);
                    if (checked) {
                      field().form.setFieldValue("end_date", null);
                    }
                  }}
                >
                  <SwitchControl>
                    <SwitchThumb />
                  </SwitchControl>
                </Switch>
                <label for="is_current">
                  {t("onboarding.steps.education.form.current")}
                </label>
              </div>
            )}
          </form.Field>
          <form.Field name="description">
            {(field) => (
              <TextFieldRoot>
                <TextFieldLabel for="description">
                  {t("onboarding.steps.education.form.description")}
                </TextFieldLabel>
                <TextArea
                  autocomplete="description"
                  id="description"
                  name={field().name}
                  value={field().state.value ?? ""}
                  onBlur={field().handleBlur}
                  onInput={(e) => field().handleChange(e.currentTarget.value)}
                  class="min-h-[100px]"
                />
                <TextFieldDescription>
                  {t("onboarding.steps.education.form.descriptionHelp")}
                </TextFieldDescription>
                <Show when={field().state.meta.errors.length > 0}>
                  <TextFieldErrorMessage>
                    {field().state.meta.errors.join(", ")}
                  </TextFieldErrorMessage>
                </Show>
              </TextFieldRoot>
            )}
          </form.Field>
          <form.Subscribe
            selector={(state) => {
              return {
                canSubmit: state.canSubmit,
                isSubmitting: state.isSubmitting,
                isValid: state.isValid,
                isDirty:
                  state.values.institution !== "" &&
                  state.values.field_of_study !== "" &&
                  state.values.degree_type !== "" &&
                  state.values.start_date !== undefined,
              };
            }}
          >
            {(state) => (
              <Button
                class="mt-8 w-full"
                onClick={form.handleSubmit}
                disabled={
                  !state().isValid || state().isSubmitting || !state().isDirty
                }
              >
                {state().isSubmitting ? t("common.saving") : t("common.save")}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </DialogContent>
    </Dialog>
  );
};
